import { Alert, Button, Divider, Stack, Typography } from "@mui/material";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { SwitchElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";

import { GqlFieldworkerUpdateInput } from "~/__generated__";
import GaiaConfirmCloseDialog from "~/modules/app/components/GaiaConfirmCloseDialog";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";
import AddressAutocompleteField from "~/modules/map/components/AddressAutocompleteField";

import useFieldworkerUpdate from "../../api/useFieldworkerUpdate";
import { USER_GET_BY_ID } from "../../api/useUserGetById";

type FieldworkerFormData = {
    address?: string | null;
    isMyWheelsUser: boolean;
    hasOwnTransport: boolean;
    hasFinishedOnboarding: boolean;
    isPayable: boolean;
    isActive: boolean;
    hasConfirmedAvailability: boolean;
};

type Props = {
    userId: string;
    fieldworker: FieldworkerFormData;
    onSubmitted: () => void;
};

export default function FieldworkerForm({ fieldworker, userId, onSubmitted }: Props) {
    const { t } = useTranslation("user");
    const { fieldworkerUpdate: updateFieldworker, error } = useFieldworkerUpdate(userId, [USER_GET_BY_ID]);

    const formMethods = useForm<Omit<GqlFieldworkerUpdateInput, "userId">>({
        defaultValues: {
            addressId: null,
            isMyWheelsUser: fieldworker.isMyWheelsUser ?? false,
            hasOwnTransport: fieldworker.hasOwnTransport ?? false,
            hasFinishedOnboarding: fieldworker.hasFinishedOnboarding ?? false,
            isPayable: fieldworker.isPayable ?? true,
            isActive: fieldworker.isActive ?? true,
            hasConfirmedAvailability: fieldworker.hasConfirmedAvailability ?? false,
        },
    });

    const {
        handleSubmit,
        setError,
        setValue,
        formState: { errors, isSubmitting, isLoading, isDirty, isSubmitted },
    } = formMethods;

    const onSubmit: SubmitHandler<Omit<GqlFieldworkerUpdateInput, "userId">> = async fieldworker => {
        await updateFieldworker(fieldworker);
        onSubmitted();
    };

    const blocker = useBlocker(!isSubmitting && !isSubmitted && isDirty);

    useApplyErrorToForm(error, setError);

    return (
        <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <GaiaConfirmCloseDialog blocker={blocker} />
                <Stack gap={2}>
                    {errors.root && (
                        <Alert severity="error">
                            <Typography>{errors.root.message}</Typography>
                        </Alert>
                    )}
                    <AddressAutocompleteField
                        fieldLabel={t("fieldworkerForm.labelAddress")}
                        noOptionsText={t("fieldworkerForm.labelAddressNoOptions")}
                        onSelect={value => setValue("addressId", value, { shouldDirty: true })}
                        initialValue={fieldworker.address}
                        disabled={false}
                    />

                    <SwitchElement name="hasOwnTransport" label={t("fieldworkerForm.labelHasOwnTransport")} />

                    <SwitchElement name="isMyWheelsUser" label={t("fieldworkerForm.labelIsMywheelsUser")} />

                    <Divider>
                        <Typography variant="caption">{t("fieldworkerForm.labelAdmin")}</Typography>
                    </Divider>

                    <SwitchElement name="hasFinishedOnboarding" label={t("fieldworkerForm.labelHasFinishedOnboarding")} />

                    <SwitchElement name="hasConfirmedAvailability" label={t("fieldworkerForm.hasConfirmedAvailability")} />

                    <SwitchElement name="isPayable" label={t("fieldworkerForm.labelIsPayable")} />

                    <SwitchElement name="isActive" label={t("fieldworkerForm.labelIsActive")} />

                    <Button type="submit" color="primary" variant="contained" disabled={!isDirty} loading={isLoading}>
                        {t("fieldworkerForm.btnSubmit")}
                    </Button>
                </Stack>
            </form>
        </FormProvider>
    );
}
